import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { RxStomp, RxStompState } from '@stomp/rx-stomp';
import { environment } from 'src/environments/environment';
import { getSessionToken } from 'src/app/utils/session-token';

export interface ChatMessage {
  uuid: string;
  text: string;
  sender: { userUuid: string; source: 'GSAT' | 'PORTAL'; displayName: string; imageUrl: string };
  createdAt: string;
}
export interface ChatEvent { eventId: string; type: string; conversationUuid: string; message?: ChatMessage; unreadCount: number; }
interface Token { accessToken: string; expiresAt: string; }
export interface ChatPage { messages: ChatMessage[]; nextCursor: string | null; hasMore: boolean; unreadCount: number; closed: boolean; }

@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private readonly http: HttpClient;
  readonly stomp = new RxStomp();
  readonly events$ = new Subject<ChatEvent>();
  readonly notifications$ = new Subject<ChatEvent>();
  readonly unread$ = new BehaviorSubject<Record<string, number>>({});
  readonly connection$ = this.stomp.connectionState$;
  private token?: Token;
  private tokenRequest?: Promise<Token>;
  private session?: string | null;
  private inbox?: Subscription;
  private connected?: Subscription;
  private renewal?: ReturnType<typeof setTimeout>;
  private seen = new Set<string>();
  private generation = 0;
  private unreadRevision = 0;
  private readonly api = environment.realtimeApiURL;

  constructor(backend: HttpBackend) {
    // Chat has independent bearer credentials; session interceptors must not attach Portal tokens to GSAT.
    this.http = new HttpClient(backend);
    this.stomp.configure({
      brokerURL: this.api.replace(/\/v1\.0\/realtime\/$/, '/ws/realtime').replace(/^http/, 'ws'),
      reconnectDelay: 5000, heartbeatIncoming: 10000, heartbeatOutgoing: 10000,
      beforeConnect: async () => {
        try { const token = await this.getToken(); this.stomp.configure({ connectHeaders: { Authorization: `Bearer ${token.accessToken}` } }); }
        catch { this.stomp.configure({ connectHeaders: {} }); }
      }
    });
  }
  start(): void {
    if (this.inbox || !getSessionToken()) return;
    this.inbox = this.stomp.watch('/user/queue/events').subscribe(frame => {
      const event = this.parse(frame.body);
      if (!event || this.seen.has(event.eventId)) return;
      this.seen.add(event.eventId);
      if (this.seen.size > 1000) this.seen.delete(this.seen.values().next().value!);
      const previousUnread = this.unread$.value[event.conversationUuid] || 0;
      this.unreadRevision++;
      this.unread$.next({ ...this.unread$.value, [event.conversationUuid]: event.unreadCount });
      this.events$.next(event);
      if (event.type === 'CONVERSATION_UPDATED' && event.unreadCount > previousUnread) this.notifications$.next(event);
    });
    this.stomp.activate();
    this.connected = this.stomp.connected$.subscribe(() => void this.synchronizeInbox());
    void this.synchronizeInbox();
  }
  stop(): void {
    this.generation++;
    this.inbox?.unsubscribe(); this.inbox = undefined;
    this.connected?.unsubscribe(); this.connected = undefined;
    void this.stomp.deactivate();
    clearTimeout(this.renewal); this.token = undefined; this.tokenRequest = undefined; this.session = undefined;
    this.seen.clear(); this.unread$.next({});
  }
  private async getToken(): Promise<Token> {
    const session = getSessionToken();
    if (!session) throw new Error('Session is missing');
    if (this.session !== session) { this.session = session; this.token = undefined; this.tokenRequest = undefined; }
    if (this.token && Date.parse(this.token.expiresAt) > Date.now() + 30000) return this.token;
    if (!this.tokenRequest) {
      const generation = this.generation;
      this.tokenRequest = firstValueFrom(this.http.post<Token>(environment.apiURL + 'realtime/auth/token', {}, {
        headers: new HttpHeaders({ 'Session-Token': session })
      })).then(token => {
        if (generation !== this.generation || session !== getSessionToken()) throw new Error('Session changed');
        this.token = token;
        clearTimeout(this.renewal);
        this.renewal = setTimeout(async () => {
          this.token = undefined;
          await this.stomp.deactivate();
          if (this.inbox && getSessionToken()) this.stomp.activate();
        }, Math.max(1000, Date.parse(token.expiresAt) - Date.now() - 15000));
        return token;
      }).finally(() => { if (generation === this.generation && this.session === session) this.tokenRequest = undefined; });
    }
    return this.tokenRequest;
  }
  private async headers(): Promise<HttpHeaders> { return new HttpHeaders({ Authorization: `Bearer ${(await this.getToken()).accessToken}` }); }
  private async synchronizeInbox(): Promise<void> {
    const generation = this.generation;
    const revision = this.unreadRevision;
    try {
      const counts = await firstValueFrom(this.http.get<Record<string, number>>(this.api + 'chat/unread', { headers: await this.headers() }));
      if (generation === this.generation) {
        if (revision === this.unreadRevision) this.unread$.next(counts);
        else void this.synchronizeInbox();
      }
    } catch { /* Reconnect retries; the repair view reports its own history errors. */ }
  }
  async history(repair: string, cursor?: string | null): Promise<ChatPage> {
    let params = new HttpParams().set('limit', 100);
    if (cursor) params = params.set('cursor', cursor);
    return firstValueFrom(this.http.get<ChatPage>(`${this.api}chat/repairs/${repair}/messages`, { headers: await this.headers(), params }));
  }
  async send(repair: string, clientMessageId: string, text: string): Promise<ChatMessage> {
    return firstValueFrom(this.http.post<ChatMessage>(`${this.api}chat/repairs/${repair}/messages`, { clientMessageId, text }, { headers: await this.headers() }));
  }
  async read(repair: string, messageUuid: string): Promise<void> {
    const generation = this.generation;
    const result = await firstValueFrom(this.http.post<{ unreadCount: number }>(`${this.api}chat/repairs/${repair}/read`, { messageUuid }, { headers: await this.headers() }));
    if (generation !== this.generation) return;
    this.unreadRevision++;
    this.unread$.next({ ...this.unread$.value, [repair]: result.unreadCount });
  }
  parse(body: string): ChatEvent | null {
    try { const event = JSON.parse(body); return event && typeof event.eventId === 'string' && typeof event.conversationUuid === 'string' ? event : null; }
    catch { return null; }
  }
}
